/**
 * MSW Overrides: Billing Receipt — Many items scenario
 *
 * Returns a single receipt with a plan, many add-ons and external charges so
 * long receipts can be checked in ReceiptPreview and the PDF.
 *
 * Enable by setting ENABLE_MSW=true and
 * NUXT_PUBLIC_MSW_OVERRIDES_BILLING_RECEIPT_MANY_ITEMS=true in your .env,
 * then restart the dev server.
 */

import type { HttpHandler } from 'msw';
import { http, HttpResponse } from 'msw';
import {
  BFF_ENDPOINTS,
  toMSWPattern,
  createMockReceipt,
  createMockReceiptItem,
  createMockReceiptAddonItem,
  createMockReceiptExternalChargeItem,
} from '@cvo/domain';
import type { CompletePrice, ReceiptResponse } from '@cvo/domain';

function eur(net: string, gross: string): CompletePrice {
  return {
    net: { amount: net, currency: 'EUR' },
    gross: { amount: gross, currency: 'EUR' },
  };
}

const ADDONS: Array<[ancillary: string, price: CompletePrice]> = [
  ['UNDERSEATBAG', eur('0.00', '0.00')],
  ['CABINBAG', eur('9.92', '12.00')],
  ['CHECKEDBAG', eur('20.66', '25.00')],
  ['SEAT', eur('6.61', '8.00')],
  ['PRIORITY', eur('4.96', '6.00')],
  ['FLEX', eur('12.40', '15.00')],
  ['INSURANCE', eur('7.44', '9.00')],
  ['LOUNGE', eur('24.79', '30.00')],
];

const EXTERNAL_CHARGES: Array<[code: string, price: CompletePrice]> = [
  ['Sign-up fee', eur('16.53', '20.00')],
  ['Renewal fee', eur('27.27', '33.00')],
  ['Late payment fee', eur('4.13', '5.00')],
];

export function createBillingReceiptManyItemsOverrides(baseUrl: string): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');

  const receipt: ReceiptResponse = createMockReceipt({
    items: [
      createMockReceiptItem({ price: eur('48.76', '59.00') }),
      ...ADDONS.map(([ancillary, price]) => createMockReceiptAddonItem({
        code: `VEL-${ancillary}-MONTHLY-PLAN`,
        product: { code: `VEL-${ancillary}-MONTHLY`, type: 'ADD_ON', ancillary },
        price,
      })),
      ...EXTERNAL_CHARGES.map(([code, price]) => createMockReceiptExternalChargeItem({ code, price })),
    ],
    subtotal: eur('183.47', '222.00'),
    total: eur('183.47', '222.00'),
    additionalCharges: [
      {
        type: 'VAT',
        code: 'VAT_EUR',
        rate: { value: '21', type: 'PERCENTAGE' },
        amount: { amount: '38.53', currency: 'EUR' },
      },
    ],
    paid: { amount: '222.00', currency: 'EUR' },
    refunded: { amount: '0.00', currency: 'EUR' },
    balance: { amount: '0.00', currency: 'EUR' },
  });

  return [
    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.RECEIPT)}`,
      () => HttpResponse.json(receipt),
    ),
  ];
}
